import React, { useEffect, useState, type PropsWithChildren } from 'react'
import type { IProjectListItemHotBonus } from '~/@api/generated-api-types-project/Api'
import { FlameIcon } from '~/@mid_ui/icons/FlameIcon'
import { useRevenueFee } from '~/@pages_investing/_utils/revenue-fee/useRevenueFee'

export type HotBonusProps = IProjectListItemHotBonus & {
  WrapperComponent?: React.FunctionComponent<PropsWithChildren<any>>
}

export function HotBonusComponent({
  yieldPerAnnum,
  activeFrom,
  activeTo,
  WrapperComponent,
}: HotBonusProps) {
  const { calculateFeeNettoAmount } = useRevenueFee()
  const [now, setNow] = useState(new Date())

  const { activeFromDate, activeToDate } = (() => {
    return {
      activeFromDate: activeFrom ? new Date(activeFrom) : null,
      activeToDate: activeTo ? new Date(activeTo) : null,
    }
  })()

  const isActive = (() =>
    yieldPerAnnum > 0 &&
    activeFromDate &&
    activeToDate &&
    now.getTime() >= activeFromDate.getTime() &&
    now.getTime() <= activeToDate.getTime())()

  useEffect(() => {
    if (!isActive) return

    const interval = setInterval(() => setNow(new Date()), 1000)
    setNow(new Date())

    return () => clearInterval(interval)
  }, [isActive])

  if (!isActive) {
    return <></>
  }

  const nettoYieldPerAnnum = (() => {
    return calculateFeeNettoAmount(yieldPerAnnum)
  })()

  const hotBonusLabel = (
    <div className="flex">
      <div className="flex w-fit items-center gap-x-[2px] rounded-l-[4px] bg-[#F0A000] px-2 text-black">
        <FlameIcon />
        <div className="text-xs" translate="no">
          +{nettoYieldPerAnnum}%
        </div>
      </div>

      <div className="flex w-fit items-center gap-x-[2px] rounded-r-[4px] bg-[#3D3D3D] px-2 text-[#E3E3E3]">
        {activeToDate != null && (
          <GetRemainingTimeString activeToDate={activeToDate} nowDate={now} />
        )}
      </div>
    </div>
  )

  if (WrapperComponent)
    return <WrapperComponent>{hotBonusLabel}</WrapperComponent>

  return hotBonusLabel
}

function GetRemainingTimeString(props: { activeToDate: Date; nowDate: Date }) {
  const diffMs = props.activeToDate.getTime() - props.nowDate.getTime()
  const totalSeconds = Math.max(0, Math.floor(diffMs / 1000))

  // Calculate total hours remaining
  const totalHours = totalSeconds / 3600

  // Determine whether to show days
  const showDays = totalHours >= 48

  // Calculate days, hours, minutes, and seconds
  const days = Math.floor(totalHours / 24)
  const hours = Math.floor(totalHours % 24)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
    .toString()
    .padStart(2, '0')
  const seconds = (totalSeconds % 60).toString().padStart(2, '0')

  return (
    <div
      className="flex w-fit items-center gap-x-[2px] rounded-r-[4px] bg-[#3D3D3D] px-2 text-[#E3E3E3]"
      translate="no">
      {showDays
        ? `${days}d:${hours
            .toString()
            .padStart(2, '0')}h:${minutes}m:${seconds}s`
        : `${Math.floor(totalHours)}h:${minutes}m:${seconds}s`}
    </div>
  )
}
